import type { ReactNode } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  Switch
} from '@tractian/ui'
import type { AssessmentFormValues } from '@/features/assessments/domain/assessment-form-schema'
import { COUNTRY_OPTIONS } from '@/features/assessments/utils/countries'

// The kit's clear control emits null; an empty string (a native select's empty
// option, or a cleared search) means the same thing: no owner.
function ownerIdOrNull(value: string | string[] | null) {
  if (Array.isArray(value)) return value[0] || null
  return value || null
}

export type AssessmentFormProps = {
  form: UseFormReturn<AssessmentFormValues>
  userOptions: { value: string; label: string }[]
  onSubmit: (values: AssessmentFormValues) => void
  mode: 'create' | 'edit'
  // multiPhase flag: the assessment carries the HubSpot Plant ID and the deal
  // reference moves to installation phases.
  multiPhase?: boolean
  // Input for the HubSpot Plant (multiPhase only). A slot because it searches
  // the API, which this pure form must not do.
  renderHubspotPlantInput: (props: HubspotInputProps) => ReactNode
  // Input for the legacy HubSpot Deal (single-phase only); a slot for the same reason.
  renderHubspotDealInput: (props: HubspotInputProps) => ReactNode
}

export type HubspotInputProps = {
  value: string | null
  onChange: (hubspotId: string | null) => void
  error: boolean
}

export function AssessmentForm({
  form,
  userOptions,
  onSubmit,
  mode,
  multiPhase = false,
  renderHubspotPlantInput,
  renderHubspotDealInput
}: AssessmentFormProps) {
  const { t } = useTranslation('plantAssessment')
  return (
    <Form {...form}>
      <form
        id="assessment-form"
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        <FormField
          control={form.control}
          name="plantName"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Plant Name *</FormLabel>
              <FormControl>
                <Input error={!!fieldState.error}>
                  <Input.Field>
                    <Input.Input {...field} placeholder="Enter plant name" />
                  </Input.Field>
                </Input>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="country"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Country *</FormLabel>
              <Select
                options={COUNTRY_OPTIONS}
                value={field.value || null}
                onValueChange={val => field.onChange(typeof val === 'string' ? val : '')}
                error={!!fieldState.error}
              >
                <FormControl>
                  <Select.Trigger>
                    <Select.Value placeholder="Select country" />
                  </Select.Trigger>
                </FormControl>
                <Select.Content>
                  <Select.Options />
                </Select.Content>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="dealOwnerUserID"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Deal Owner</FormLabel>
              <Select
                options={userOptions}
                value={field.value ?? null}
                onValueChange={val => field.onChange(ownerIdOrNull(val))}
                showSearch
              >
                <FormControl>
                  <Select.Trigger>
                    <Select.Value placeholder="Select deal owner" allowClear />
                  </Select.Trigger>
                </FormControl>
                <Select.Content>
                  <Select.Options />
                </Select.Content>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="salesEngineerUserID"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Sales Engineer</FormLabel>
              <Select
                options={userOptions}
                value={field.value ?? null}
                onValueChange={val => field.onChange(ownerIdOrNull(val))}
                showSearch
              >
                <FormControl>
                  <Select.Trigger>
                    <Select.Value placeholder="Select sales engineer" allowClear />
                  </Select.Trigger>
                </FormControl>
                <Select.Content>
                  <Select.Options />
                </Select.Content>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isAtex"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between">
              <FormLabel>ATEX</FormLabel>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange}>
                  <Switch.Thumb />
                </Switch>
              </FormControl>
            </FormItem>
          )}
        />

        {multiPhase ? (
          <FormField
            control={form.control}
            name="hubspotPlantId"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('assessmentFormModal.hubspotPlant.label')}</FormLabel>
                <FormControl>
                  {renderHubspotPlantInput({
                    value: field.value ?? null,
                    onChange: field.onChange,
                    error: !!fieldState.error
                  })}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : (
          <FormField
            control={form.control}
            name="hubspotDealId"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('installationPhaseForm.hubspotDealId.label')}</FormLabel>
                <FormControl>
                  {renderHubspotDealInput({
                    value: field.value ?? null,
                    onChange: field.onChange,
                    error: !!fieldState.error
                  })}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {mode === 'edit' && (
          <FormField
            control={form.control}
            name="assessedAt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Assessment Date</FormLabel>
                <FormControl>
                  <Input>
                    <Input.Field>
                      <Input.Input
                        type="date"
                        value={field.value ?? ''}
                        onChange={e => field.onChange(e.target.value || null)}
                      />
                    </Input.Field>
                  </Input>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {mode === 'edit' && (
          <FormField
            control={form.control}
            name="lastOnsiteAt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('assessmentFormModal.lastOnsiteAt.label')}</FormLabel>
                <FormControl>
                  <Input>
                    <Input.Field>
                      <Input.Input
                        type="date"
                        value={field.value ?? ''}
                        onChange={e => field.onChange(e.target.value || null)}
                      />
                    </Input.Field>
                  </Input>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </form>
    </Form>
  )
}
